import { motion } from "framer-motion";
import { HeartHandshake, Eye, Sun } from "lucide-react";
import { PORTRAIT_URL } from "../config";
import { useSite, resolveMedia } from "../siteContext";
import { useLang, pick } from "../i18n";
import { SectionBackdrop } from "./SectionBackdrop";

const ICONS = [HeartHandshake, Eye, Sun];

export const About = () => {
  const settings = useSite();
  const { lang, s } = useLang();
  const DESIRES = s.desires.map((text, i) => ({ icon: ICONS[i], text }));
  return (
  <section id="about" data-testid="about-section" className="relative py-28 md:py-36 bg-[var(--c-light-bg,#F8F6F0)] text-[var(--c-light-text,#1B0E23)] border-y border-[var(--c-light-border,#E8E2D8)]">
    <SectionBackdrop id="about" tone="light" />
    <div className="relative max-w-7xl mx-auto px-4 md:px-8 grid grid-cols-1 lg:grid-cols-12 gap-14 items-center">
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
        className="lg:col-span-7"
      >
        <p className="text-[#A8823F] text-sm tracking-[0.35em] mb-6">{s.about_eyebrow}</p>
        <h2 data-testid="about-title" className="font-serif font-bold text-4xl sm:text-5xl lg:text-6xl leading-[1.25] mb-8">
          <span className="text-[var(--c-light-text,#1A102F)] italic">{pick(lang, settings, "about_title")}</span>
        </h2>
        <p data-testid="about-text" className="text-base md:text-lg text-[var(--c-light-muted,#605670)] leading-loose mb-10 max-w-2xl">
          {pick(lang, settings, "about_text")}
        </p>

        <p className="text-[var(--c-light-text,#1A102F)] font-serif text-2xl mb-6">{s.about_you_want}</p>
        <div className="space-y-5">
          {DESIRES.map((d, i) => (
            <motion.div
              key={i}
              data-testid={`about-desire-${i}`}
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.12, duration: 0.7 }}
              className="flex items-center gap-5 border-b border-[var(--c-light-border,#E8E2D8)] pb-5"
            >
              <d.icon className="w-6 h-6 text-[#D1A05C] shrink-0" strokeWidth={1.25} />
              <p className="text-lg text-[var(--c-light-text,#1A102F)]">{d.text}</p>
            </motion.div>
          ))}
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        whileInView={{ opacity: 1, scale: 1 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
        className="lg:col-span-5 lg:-translate-y-10"
      >
        <div className="relative">
          <div className="overflow-hidden border border-[var(--c-light-border,#E8E2D8)] rounded-3xl aspect-[4/5] max-h-[560px] shadow-[0_30px_70px_rgba(26,16,47,0.14)]">
            <img
              data-testid="about-portrait"
              src={resolveMedia(settings.about_image || settings.portrait_url) || PORTRAIT_URL}
              alt={s.about_alt}
              className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-700"
            />
          </div>
          <div className="absolute -bottom-6 -right-6 bg-white border border-[var(--c-light-border,#E8E2D8)] px-8 py-6 shadow-xl">
            <p className="font-serif text-3xl text-[var(--c-light-text,#1A102F)] italic">{s.about_name}</p>
            <p className="text-xs text-[var(--c-light-muted,#605670)] tracking-[0.25em] mt-1">{s.about_role}</p>
          </div>
        </div>
      </motion.div>
    </div>
  </section>
  );
};
